// Server history — favorites and recent servers stored in localStorage

const STORAGE_KEY = "ironbolt_server_history";

interface ServerHistoryEntry {
  name: string;
  mapName: string;
  lastJoined: number;  // timestamp
  favorite: boolean;
}

function load(): Map<string, ServerHistoryEntry> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Map();
    const arr: ServerHistoryEntry[] = JSON.parse(raw);
    return new Map(arr.map(e => [e.name, e]));
  } catch { return new Map(); }
}

function save(entries: Map<string, ServerHistoryEntry>): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(entries.values())));
}

/** Record that the player joined a server. */
export function recordJoin(name: string, mapName: string): void {
  const entries = load();
  const existing = entries.get(name);
  entries.set(name, {
    name,
    mapName,
    lastJoined: Date.now(),
    favorite: existing?.favorite ?? false,
  });
  // Keep only the 20 most recent non-favorite entries
  const sorted = Array.from(entries.values())
    .filter(e => !e.favorite)
    .sort((a, b) => b.lastJoined - a.lastJoined);
  if (sorted.length > 20) {
    for (const old of sorted.slice(20)) entries.delete(old.name);
  }
  save(entries);
}

/** Toggle favorite status for a server. */
export function toggleFavorite(name: string, mapName: string): boolean {
  const entries = load();
  const existing = entries.get(name);
  const isFav = !(existing?.favorite ?? false);
  entries.set(name, {
    name,
    mapName,
    lastJoined: existing?.lastJoined ?? Date.now(),
    favorite: isFav,
  });
  save(entries);
  return isFav;
}

/** Get all favorite server names. */
export function getFavorites(): Set<string> {
  const entries = load();
  const favs = new Set<string>();
  for (const [name, entry] of entries) {
    if (entry.favorite) favs.add(name);
  }
  return favs;
}

/** Get recent server names (most recent first, excludes favorites). */
export function getRecents(): string[] {
  const entries = load();
  return Array.from(entries.values())
    .filter(e => !e.favorite)
    .sort((a, b) => b.lastJoined - a.lastJoined)
    .map(e => e.name);
}
